/**
 * Mega Menu Drawer
 * Custom element for handling the mega menu interactions including:
 * - Opening/closing the drawer
 * - Category submenu navigation
 * - Desktop quick link integration
 * - Keyboard navigation and accessibility
 */

if (typeof window !== 'undefined') {
  class MegaMenuDrawer extends HTMLElement {
    constructor() {
      super();
      
      this.details = this.querySelector('#MegaMenuDrawer');
      this.summary = this.querySelector('.mega-menu-drawer__toggle');
      this.panel = this.querySelector('.mega-menu-drawer__panel');
      this.overlay = this.querySelector('[data-overlay]') || document.getElementById('MegaMenuBackdrop');
      this.mainView = this.querySelector('.mega-menu-drawer__main-view');
      // Quick links can be inside or outside the custom element (in header)
      this.quickLinks = document.querySelectorAll('.mega-menu-drawer__quick-link');
      
      // Extract submenu panels from templates and move to submenu container
      this.extractSubmenuPanels();
      
      this.categoryTriggers = this.querySelectorAll('.mega-menu-drawer__category-trigger');
      this.categoryPanels = this.querySelectorAll('.mega-menu-drawer__submenu');
      this.backButtons = this.querySelectorAll('.mega-menu-drawer__back-button');
      
      this.activeCategory = null;
      this.isAnimating = false;
      
      this.bindEvents();
    }

    /**
     * Extract submenu panels from template elements and move them to the submenu container.
     * This allows blocks to define their own submenu content while keeping proper DOM structure.
     */
    extractSubmenuPanels() {
      const templates = this.querySelectorAll('template[data-category-submenu]');
      const container = this.querySelector('[data-submenu-container]');
      
      if (!container) return;
      
      templates.forEach((template) => {
        const content = template.content.cloneNode(true);
        container.appendChild(content);
      });
    }

    bindEvents() {
      // Main toggle
      if (this.summary) {
        this.summary.addEventListener('click', this.onToggleClick.bind(this));
      }

      // Category triggers
      this.categoryTriggers.forEach((trigger) => {
        trigger.addEventListener('click', this.onCategoryClick.bind(this));
      });

      // Back buttons
      this.backButtons.forEach((button) => {
        button.addEventListener('click', this.onBackClick.bind(this));
      });

      // Quick links (desktop)
      this.quickLinks.forEach((link) => {
        link.addEventListener('click', this.onQuickLinkClick.bind(this));
      });

      // Close on escape key
      document.addEventListener('keydown', this.onKeyDown.bind(this));

      // Close when clicking overlay (listener on overlay so click always works)
      if (this.overlay) {
        this.overlay.addEventListener('click', () => {
          if (this.details?.open) this.close();
        });
      }

      // Handle details toggle for accessibility
      if (this.details) {
        this.details.addEventListener('toggle', this.onDetailsToggle.bind(this));
      }
    }

    onToggleClick(event) {
      event.preventDefault();
      
      if (this.details.open) {
        // If a category is open (opened via quicklink), go back to main view instead of closing
        if (this.activeCategory) {
          this.closeAllCategories();
          
          // Reset quicklinks and set hamburger as active
          this.quickLinks.forEach((link) => {
            link.setAttribute('aria-expanded', 'false');
          });
          
          if (this.summary) {
            this.summary.setAttribute('aria-expanded', 'true');
          }
        } else {
          // Main view is showing, close the drawer
          this.close();
        }
      } else {
        // Reset quicklinks when opening via hamburger
        this.quickLinks.forEach((link) => {
          link.setAttribute('aria-expanded', 'false');
        });
        
        this.open();
        
        // Set hamburger as expanded only when opened via hamburger click
        if (this.summary) {
          this.summary.setAttribute('aria-expanded', 'true');
        }
      }
    }

    open(targetCategoryId = null) {
      if (this.isAnimating) return;
      
      this.isAnimating = true;

      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer?.classList.contains('active')) {
        cartDrawer.close();
      }
      
      // If opening via quicklink (with category), ensure hamburger stays collapsed
      // If opening via hamburger, aria-expanded is set in onToggleClick
      if (targetCategoryId && this.summary) {
        this.summary.setAttribute('aria-expanded', 'false');
      }
      
      // Prevent body scroll; pause header auto-hide (same idea as overlay-open)
      document.body.style.overflow = 'hidden';
      document.body.classList.add('mega-menu-open');
      
      // Reset any open categories first
      this.closeAllCategories();
      
      // If targeting a specific category, set it up BEFORE opening the drawer
      // so it appears instantly without animation
      if (targetCategoryId) {
        this.openCategoryInstant(targetCategoryId);
      }
      
      // Now open the drawer
      this.details.open = true;

      this.overlay?.classList.add('is-visible');

      // Focus trap setup
      this.trapFocus();
      
      setTimeout(() => {
        this.isAnimating = false;
      }, 400);
    }

    close() {
      if (this.isAnimating) return;
      
      this.isAnimating = true;
      
      // Close any open categories first
      this.closeAllCategories();
      
      // Close the drawer
      this.details.open = false;

      this.overlay?.classList.remove('is-visible');

      // Restore body scroll
      document.body.style.overflow = '';
      document.body.classList.remove('mega-menu-open');
      
      // Reset hamburger state
      if (this.summary) {
        this.summary.setAttribute('aria-expanded', 'false');
      }
      
      // Reset quick links state
      this.quickLinks.forEach((link) => {
        link.setAttribute('aria-expanded', 'false');
      });
      
      setTimeout(() => {
        this.isAnimating = false;
      }, 400);
    }

    onCategoryClick(event) {
      const trigger = event.currentTarget;
      const categoryId = trigger.dataset.categoryId;
      
      if (this.activeCategory === categoryId) {
        this.closeCategory(categoryId);
      } else {
        this.openCategory(categoryId);
      }
    }

    openCategory(categoryId, skipAnimationCheck = false) {
      // Skip animation check when opening from quick links during drawer open
      if (this.isAnimating && !skipAnimationCheck) return;
      
      // Close any previously open category
      if (this.activeCategory) {
        this.closeCategory(this.activeCategory, false);
      }
      
      const panel = this.querySelector(`[data-category-panel="${categoryId}"]`);
      const trigger = this.querySelector(`[data-category-id="${categoryId}"]`);
      
      if (!panel) {
        console.warn('Mega Menu: Category panel not found for ID:', categoryId);
        return;
      }
      
      this.activeCategory = categoryId;
      
      // Update trigger state if it exists
      if (trigger) {
        trigger.setAttribute('aria-expanded', 'true');
      }
      
      // Animate main view out
      if (this.mainView) {
        this.mainView.classList.add('is-hidden');
      }
      
      // Open the panel
      panel.classList.add('is-open');
      panel.setAttribute('aria-hidden', 'false');
      
      // Update quick link state if applicable (quick links are outside the custom element in the header)
      const quickLink = document.querySelector(`[data-open-category="${categoryId}"]`);
      if (quickLink) {
        quickLink.setAttribute('aria-expanded', 'true');
      }
      
      // Focus the back button for accessibility
      const backButton = panel.querySelector('.mega-menu-drawer__back-button');
      if (backButton) {
        setTimeout(() => {
          backButton.focus();
        }, 400);
      }
    }

    /**
     * Opens a category instantly without any animation.
     * Used when opening drawer directly to a specific category via quick links.
     */
    openCategoryInstant(categoryId) {
      const panel = this.querySelector(`[data-category-panel="${categoryId}"]`);
      const trigger = this.querySelector(`[data-category-id="${categoryId}"]`);
      
      if (!panel) {
        console.warn('Mega Menu: Category panel not found for ID:', categoryId);
        return;
      }
      
      this.activeCategory = categoryId;
      
      // Update trigger state if it exists
      if (trigger) {
        trigger.setAttribute('aria-expanded', 'true');
      }
      
      // Hide main view instantly (no animation)
      if (this.mainView) {
        this.mainView.classList.add('is-hidden');
        this.mainView.style.display = 'none';
      }
      
      // Open the panel instantly (no animation)
      panel.classList.add('is-open', 'is-instant');
      panel.setAttribute('aria-hidden', 'false');
      
      // Update quick link state if applicable (quick links are outside the custom element in the header)
      const quickLink = document.querySelector(`[data-open-category="${categoryId}"]`);
      if (quickLink) {
        quickLink.setAttribute('aria-expanded', 'true');
      }
      
      // Focus the back button for accessibility after drawer opens
      const backButton = panel.querySelector('.mega-menu-drawer__back-button');
      if (backButton) {
        setTimeout(() => {
          backButton.focus();
        }, 400);
      }
    }

    closeCategory(categoryId, animateMainView = true) {
      const panel = this.querySelector(`[data-category-panel="${categoryId}"]`);
      const trigger = this.querySelector(`[data-category-id="${categoryId}"]`);
      
      if (!panel) return;
      
      // Update trigger state
      if (trigger) {
        trigger.setAttribute('aria-expanded', 'false');
      }
      
      // Close the panel and remove instant class
      panel.classList.remove('is-open', 'is-instant');
      panel.setAttribute('aria-hidden', 'true');
      
      // Restore main view
      if (animateMainView && this.mainView) {
        this.mainView.style.display = '';
        this.mainView.classList.remove('is-hidden');
      }
      
      // Update quick link state (quick links are outside the custom element in the header)
      const quickLink = document.querySelector(`[data-open-category="${categoryId}"]`);
      if (quickLink) {
        quickLink.setAttribute('aria-expanded', 'false');
      }
      
      if (this.activeCategory === categoryId) {
        this.activeCategory = null;
      }
    }

    closeAllCategories() {
      this.categoryPanels.forEach((panel) => {
        panel.classList.remove('is-open', 'is-instant');
        panel.setAttribute('aria-hidden', 'true');
      });
      
      this.categoryTriggers.forEach((trigger) => {
        trigger.setAttribute('aria-expanded', 'false');
      });
      
      if (this.mainView) {
        this.mainView.style.display = '';
        this.mainView.classList.remove('is-hidden');
      }
      
      this.activeCategory = null;
    }

    onBackClick(event) {
      const button = event.currentTarget;
      const categoryId = button.dataset.closeCategory;
      
      if (categoryId) {
        this.closeCategory(categoryId);
        
        // Focus the category trigger for accessibility
        const trigger = this.querySelector(`[data-category-id="${categoryId}"]`);
        if (trigger) {
          setTimeout(() => {
            trigger.focus();
          }, 100);
        }
      }
    }

    onQuickLinkClick(event) {
      const link = event.currentTarget;
      const categoryId = link.dataset.openCategory;
      
      if (!categoryId) return;
      
      // When quicklink is clicked, hamburger should not be expanded
      if (this.summary) {
        this.summary.setAttribute('aria-expanded', 'false');
      }
      
      // If drawer is closed, open it and navigate to category
      if (!this.details.open) {
        this.open(categoryId);
      } else {
        // If drawer is open, just navigate to category
        this.openCategory(categoryId);
      }
    }

    onKeyDown(event) {
      if (event.key === 'Escape' && this.details.open) {
        // If a category is open, close it first
        if (this.activeCategory) {
          this.closeCategory(this.activeCategory);
        } else {
          // Otherwise close the whole drawer
          this.close();
          this.summary?.focus();
        }
      }
    }

    onDetailsToggle() {
      this.overlay?.classList.toggle('is-visible', this.details.open);
      if (!this.details.open) {
        // Cleanup when closed via native toggle
        document.body.style.overflow = '';
        document.body.classList.remove('mega-menu-open');
        this.closeAllCategories();
        
        // Reset hamburger state
        if (this.summary) {
          this.summary.setAttribute('aria-expanded', 'false');
        }
        
        // Reset quick links state
        this.quickLinks.forEach((link) => {
          link.setAttribute('aria-expanded', 'false');
        });
      }
    }

    trapFocus() {
      // Get all focusable elements in the panel
      const focusableElements = this.panel?.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      
      if (!focusableElements || focusableElements.length === 0) return;
      
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      
      // Focus the first element
      firstElement?.focus();
      
      // Handle tab key to trap focus
      this.panel?.addEventListener('keydown', (event) => {
        if (event.key !== 'Tab') return;
        
        if (event.shiftKey) {
          // Shift + Tab
          if (document.activeElement === firstElement) {
            event.preventDefault();
            lastElement?.focus();
          }
        } else {
          // Tab
          if (document.activeElement === lastElement) {
            event.preventDefault();
            firstElement?.focus();
          }
        }
      });
    }
  }

  // Define the custom element
  if (!customElements.get('mega-menu-drawer')) {
    customElements.define('mega-menu-drawer', MegaMenuDrawer);
  }
}
